import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { AgGridReact } from 'ag-grid-react';
import {
  BUTTON_SIZES,
  CELL_RENDERERS,
  IconOpenInNew, Link,
  STATUS_INDICATOR_VARIANTS,
  StatusIndicator,
  TableContainer,
  TableContent,
  TableFooter,
  TableHeader,
  TextButton,
  TextRenderer
} from 'cdk-radial';

import './index.scss';

const AddendumLink = styled.a`
  color: ${({ theme }) => theme.color.primary[500].value};
`;
const NewApiIndicator = styled(StatusIndicator)`
    margin-left: 0.5rem;
`;
const AlignedOpenInNew = styled(IconOpenInNew)`
    position: relative;
    top: 0.2rem;
    margin-left: 0.125rem;
`;
const ActionLinkBase = styled(Link)`
    font-weight: ${({ theme }) => theme.font.weight.medium.value};
    :hover {
        cursor: pointer;
    }
`;
const TableActionButton = styled(TextButton)`
    position: relative;
    left: -0.5rem;
`;

function ActionLink(props) {
  const { onClick, ariaLabel, ...otherProps } = props;
  return <ActionLinkBase
    {...otherProps}
    role="button"
    aria-label={ariaLabel || otherProps.text}
    onClick={(...args) => {
      args[0].preventDefault();
      return onClick(...args);
    }}
  />;
}

const frameworkComponentsExtended = {
  textRenderer: TextRenderer
};

/**
 * @type (import('ag-grid-community').ColDef)[]
 */
const columns = [
  {
    cellRendererFramework: ApiNameCell,
    field: 'apiDisplayName',
    headerName: 'API',
    sortable: true
  },
  {
    cellRenderer: CELL_RENDERERS.TEXT,
    field: 'name',
    headerName: 'Publisher',
    sortable: true
  },
  {
    cellRendererFramework: FieldsCell,
    headerName: 'Fields',
    minWidth: 120,
    width: 120,
    suppressSizeToFit: true
  },
  {
    cellRendererFramework: ActionsCell,
    headerName: 'Action',
    minWidth: 120,
    width: 120,
    suppressSizeToFit: true
  }
];

function ApiNameCell(props) {
  const {
    data: { addendumDoc, isNewApi, isBulkSubscribed },
  } = props;
  return (
    <>
      {addendumDoc
        ? (
          <AddendumLink
            title="View integration addendum in a new tab"
            href={addendumDoc}
            target="_blank"
            rel="noreferrer"
            aria-label="View integration addendum in a new tab"
          >
            <TextRenderer {...props} />
            <AlignedOpenInNew width={16} height={16} />
          </AddendumLink>
        )
        : <TextRenderer {...props} />
      }
      {!isBulkSubscribed && isNewApi ? (
        <NewApiIndicator
          label="New"
          variant={STATUS_INDICATOR_VARIANTS.INFORMATION}
        />
      ) : null}
    </>
  );
}

function FieldsCell({ data, context }) {
  const { index, fields } = data;
  const { onShowFields } = context;
  return fields?.length ? (
    <ActionLink
      ariaLabel={`${fields.length} fields`}
      text={`${fields.length}`}
      onClick={() => onShowFields(index)}
    />
  ) : null;
}

function ActionsCell({ data }) {
  const {
    onManageConnections,
    isNewApi,
    isBulkSubscribed,
    originalApiProvider,
    isAppNotListed
  } = data;

  return <TableActionButton
    className="api-integration__action-button"
    isDisabled={isBulkSubscribed && isAppNotListed}
    text="Manage"
    size={BUTTON_SIZES.SMALL}
    onClick={() => onManageConnections({
      apiProvider: originalApiProvider,
      isNewApi
    })}
  />;
}

export default function ApiIntegrationsTable({
  apiProviders,
  locationsGroupByApi,
  solutionData,
  subscriptionData,
  isBulkSubscribed,
  onShowFields,
  onManageConnections,
  solution
}) {
  
  const [searchValue, setSearchValue] = useState('');
  const [gridApi, setGridApi] = useState();

  const handleGridReady = useCallback(gridParams => {
    setGridApi(gridParams.api);
    gridParams.api.sizeColumnsToFit();
  }, []);

  const rowData = apiProviders
    .map((apiProvider, index) => {
      const {
        api = '',
        addendumDoc
      } = apiProvider;
      const numProviders = locationsGroupByApi[api]?.length || 0;

      // if integration addendum is overridden by app admin settings,
      // link to that addendum instead
      const addendumDocLink = solution?.custom_consent && (
        !solution?.custom_consent?.selectedApis ||
        solution?.custom_consent?.selectedApis.includes(
          api.replace('api-v2-','')
        )
      )
        ? solution.custom_consent.customConsentForm
        : addendumDoc;

      if (apiProvider) {
        return {
          ...apiProvider,
          index,
          addendumDoc: addendumDocLink,
          onManageConnections,
          isNewApi: numProviders < subscriptionData.length,
          isBulkSubscribed,
          isAppNotListed: solutionData?.isAppNotListed,
          originalApiProvider: apiProvider
        };
      }
      return null;
    })
    .filter(p => p);

  return (
    <TableContainer className='api-integrations-table ag-theme-balham'>
      <TableHeader
        id='api-integrations-table-header'
        searchInputName='api-integrations-search'
        searchInputValue={searchValue}
        onSearchInputChange={v => {
          setSearchValue(v);
          gridApi.setQuickFilter(v);
        }}
        searchPlaceholder='Find an API'
        isPrintable={false}
        isDownloadable={false}
      />
      <TableContent>
        <AgGridReact
          columnDefs={columns}
          rowData={rowData}
          context={{ onShowFields }}
          frameworkComponents={frameworkComponentsExtended}
          onGridReady={handleGridReady}
          rowHeight={40}
          domLayout='autoHeight'
        />
      </TableContent>
      <TableFooter />
    </TableContainer>
  );
}
